import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const LegalModal = ({ isOpen, onClose, type }) => {
  if (!isOpen) return null;

  const content = {
    cookies: {
      title: "Política de Cookies",
      body: () => (
        <div className="space-y-6 text-[#A1A1AA] text-sm leading-relaxed">
          <p><strong className="text-white">Última actualización:</strong> Enero 2026</p>

          <div>
            <h3 className="text-white font-medium text-base mb-2">1. ¿Qué son las cookies?</h3>
            <p>Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo cuando visitas una página web. Se utilizan para recordar tus preferencias, analizar el uso de la web y mejorar tu experiencia de navegación.</p>
          </div>

          <div>
            <h3 className="text-white font-medium text-base mb-2">2. ¿Quién es el responsable?</h3>
            <p>El responsable del tratamiento de los datos recogidos a través de las cookies es <strong className="text-white">Julián Digital Studio</strong>.</p>
            <p>Email de contacto: <strong className="text-white">julianwebsstudio@gmail.com</strong></p>
          </div>

          <div>
            <h3 className="text-white font-medium text-base mb-2">3. Tipos de cookies que utilizamos</h3>
            <div className="space-y-3">
              <div className="p-4 rounded-lg border border-white/10 bg-white/[0.02]">
                <p className="text-white font-medium mb-1">Cookies esenciales (técnicas)</p>
                <p>Son necesarias para el correcto funcionamiento de la web. Incluyen el almacenamiento de tus preferencias de cookies. No requieren consentimiento.</p>
                <p className="mt-1 text-xs text-[#D4AF37]">Duración: sesión / 24 meses</p>
              </div>
              <div className="p-4 rounded-lg border border-white/10 bg-white/[0.02]">
                <p className="text-white font-medium mb-1">Cookies analíticas</p>
                <p>Nos ayudan a comprender cómo interactúan los visitantes con la web, proporcionando información sobre las áreas visitadas, el tiempo de navegación y cualquier problema encontrado. Son anónimas.</p>
                <p className="mt-1 text-xs text-[#D4AF37]">Duración: hasta 24 meses</p>
              </div>
              <div className="p-4 rounded-lg border border-white/10 bg-white/[0.02]">
                <p className="text-white font-medium mb-1">Cookies de marketing</p>
                <p>Se utilizan para rastrear a los visitantes en las páginas web con la intención de mostrar anuncios relevantes y personalizados para el usuario.</p>
                <p className="mt-1 text-xs text-[#D4AF37]">Duración: hasta 24 meses</p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-white font-medium text-base mb-2">4. Base legal</h3>
            <p>La base legal para el tratamiento de datos a través de cookies esenciales es el interés legítimo del responsable (art. 6.1.f RGPD). Para las cookies analíticas y de marketing, la base legal es tu consentimiento (art. 6.1.a RGPD), que puedes gestionar desde el banner de cookies o contactándonos.</p>
          </div>

          <div>
            <h3 className="text-white font-medium text-base mb-2">5. ¿Cómo gestionar las cookies?</h3>
            <p>Puedes aceptar, rechazar o configurar las cookies desde el banner que aparece al acceder a la web. También puedes modificar tus preferencias en cualquier momento desde la configuración de tu navegador.</p>
            <p className="mt-2">Para eliminar cookies de tu navegador, consulta la ayuda de:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Google Chrome</li>
              <li>Mozilla Firefox</li>
              <li>Safari</li>
              <li>Microsoft Edge</li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-medium text-base mb-2">6. Tus derechos</h3>
            <p>Puedes ejercer tus derechos de acceso, rectificación, supresión, oposición, limitación y portabilidad escribiendo a <strong className="text-white">julianwebsstudio@gmail.com</strong>. También puedes presentar una reclamación ante la Agencia Española de Protección de Datos (AEPD) en <strong className="text-white">www.aepd.es</strong>.</p>
          </div>
        </div>
      ),
    },
    privacy: {
      title: "Política de Privacidad",
      body: () => (
        <div className="space-y-6 text-[#A1A1AA] text-sm leading-relaxed">
          <p><strong className="text-white">Última actualización:</strong> Enero 2026</p>

          <div>
            <h3 className="text-white font-medium text-base mb-2">1. Responsable del tratamiento</h3>
            <p><strong className="text-white">Identidad:</strong> Julián Digital Studio</p>
            <p><strong className="text-white">Email:</strong> julianwebsstudio@gmail.com</p>
          </div>

          <div>
            <h3 className="text-white font-medium text-base mb-2">2. ¿Qué datos recogemos?</h3>
            <p>A través del formulario de diagnóstico web, recogemos los siguientes datos:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Tipo de negocio</li>
              <li>Ubicación (ciudad)</li>
              <li>Objetivo de la web</li>
              <li>Estado actual de la web</li>
              <li>Preferencia de contacto</li>
              <li>Nombre</li>
              <li>Dato de contacto (WhatsApp o email)</li>
              <li>Notas adicionales (opcional)</li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-medium text-base mb-2">3. ¿Para qué usamos tus datos?</h3>
            <ul className="list-disc list-inside space-y-1">
              <li>Responder a tu consulta y proporcionarte una orientación personalizada sobre nuestros servicios de diseño web.</li>
              <li>Contactarte por el medio que hayas indicado (WhatsApp o email).</li>
              <li>Gestión interna de clientes potenciales.</li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-medium text-base mb-2">4. Base legal del tratamiento</h3>
            <p><strong className="text-white">Consentimiento del interesado</strong> (art. 6.1.a RGPD): al enviar el formulario de diagnóstico, consientes el tratamiento de tus datos para las finalidades indicadas.</p>
            <p className="mt-2"><strong className="text-white">Interés legítimo</strong> (art. 6.1.f RGPD): para la gestión de relaciones comerciales derivadas de tu solicitud.</p>
          </div>

          <div>
            <h3 className="text-white font-medium text-base mb-2">5. ¿Durante cuánto tiempo conservamos tus datos?</h3>
            <p>Conservamos tus datos durante el tiempo necesario para atender tu consulta y, en su caso, durante la relación comercial. Una vez finalizada, se conservarán bloqueados durante los plazos legalmente establecidos.</p>
          </div>

          <div>
            <h3 className="text-white font-medium text-base mb-2">6. ¿A quién comunicamos tus datos?</h3>
            <p>Los datos enviados a través del formulario son procesados por <strong className="text-white">Web3Forms</strong> como encargado del tratamiento para la recepción del formulario. No cedemos tus datos a terceros salvo obligación legal.</p>
          </div>

          <div>
            <h3 className="text-white font-medium text-base mb-2">7. Tus derechos</h3>
            <p>De acuerdo con el RGPD y la LOPDGDD, tienes derecho a:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li><strong className="text-white">Acceso:</strong> conocer qué datos tenemos sobre ti.</li>
              <li><strong className="text-white">Rectificación:</strong> corregir datos inexactos.</li>
              <li><strong className="text-white">Supresión:</strong> solicitar la eliminación de tus datos.</li>
              <li><strong className="text-white">Oposición:</strong> oponerte al tratamiento de tus datos.</li>
              <li><strong className="text-white">Limitación:</strong> solicitar que se limite el tratamiento.</li>
              <li><strong className="text-white">Portabilidad:</strong> recibir tus datos en formato estructurado.</li>
            </ul>
            <p className="mt-2">Para ejercer estos derechos, escribe a <strong className="text-white">julianwebsstudio@gmail.com</strong>.</p>
            <p className="mt-2">Si consideras que no hemos tratado tus datos adecuadamente, puedes presentar una reclamación ante la <strong className="text-white">Agencia Española de Protección de Datos (www.aepd.es)</strong>.</p>
          </div>

          <div>
            <h3 className="text-white font-medium text-base mb-2">8. Seguridad</h3>
            <p>Aplicamos medidas técnicas y organizativas adecuadas para proteger tus datos personales frente a accesos no autorizados, pérdida o destrucción.</p>
          </div>
        </div>
      ),
    },
    legal: {
      title: "Aviso Legal",
      body: () => (
        <div className="space-y-6 text-[#A1A1AA] text-sm leading-relaxed">
          <p><strong className="text-white">Última actualización:</strong> Enero 2026</p>

          <div>
            <h3 className="text-white font-medium text-base mb-2">1. Datos identificativos</h3>
            <p>En cumplimiento del artículo 10 de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y Comercio Electrónico (LSSI-CE), se facilitan los siguientes datos:</p>
            <div className="mt-3 p-4 rounded-lg border border-white/10 bg-white/[0.02]">
              <p><strong className="text-white">Denominación:</strong> Julián Digital Studio</p>
              <p><strong className="text-white">Email:</strong> julianwebsstudio@gmail.com</p>
              <p><strong className="text-white">Actividad:</strong> Diseño y desarrollo de páginas web para negocios locales</p>
            </div>
          </div>

          <div>
            <h3 className="text-white font-medium text-base mb-2">2. Objeto</h3>
            <p>El presente sitio web tiene como finalidad informar sobre los servicios de diseño web ofrecidos por Julián Digital Studio, así como facilitar el contacto con potenciales clientes a través de un formulario de diagnóstico guiado.</p>
          </div>

          <div>
            <h3 className="text-white font-medium text-base mb-2">3. Propiedad intelectual e industrial</h3>
            <p>Todos los contenidos de esta web (textos, imágenes, diseño gráfico, código fuente, logos, marcas) son propiedad de Julián Digital Studio o de sus legítimos propietarios, y están protegidos por la legislación vigente de propiedad intelectual e industrial.</p>
            <p className="mt-2">Queda prohibida la reproducción, distribución, comunicación pública o transformación de estos contenidos sin autorización expresa.</p>
          </div>

          <div>
            <h3 className="text-white font-medium text-base mb-2">4. Condiciones de uso</h3>
            <p>El usuario se compromete a hacer un uso adecuado de los contenidos y servicios que ofrece este sitio web, absteniéndose de:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Utilizarlos para actividades ilícitas o contrarias a la buena fe.</li>
              <li>Provocar daños en los sistemas del prestador o de terceros.</li>
              <li>Introducir virus o programas maliciosos.</li>
              <li>Intentar acceder a cuentas de otros usuarios.</li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-medium text-base mb-2">5. Exclusión de responsabilidad</h3>
            <p>Julián Digital Studio no se hace responsable de:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>La falta de disponibilidad o continuidad del funcionamiento de la web.</li>
              <li>Los daños que pudieran producirse por un uso inadecuado del sitio.</li>
              <li>Los contenidos y servicios ofrecidos por terceros a través de enlaces desde esta web.</li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-medium text-base mb-2">6. Legislación aplicable y jurisdicción</h3>
            <p>Las relaciones establecidas entre Julián Digital Studio y el usuario se regirán por la legislación española. Para cualquier controversia, las partes se someterán a los juzgados y tribunales correspondientes.</p>
          </div>
        </div>
      ),
    },
  };

  const current = content[type];
  if (!current) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm"></div>
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.98 }}
          transition={{ duration: 0.3 }}
          className="relative bg-[#0A0A0A] border border-white/10 rounded-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid={`legal-modal-${type}`}
        >
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <h2 className="text-xl font-medium text-white">{current.title}</h2>
            <button
              onClick={onClose}
              className="text-[#A1A1AA] hover:text-white transition-colors p-1"
              data-testid="legal-modal-close"
            >
              <X size={24} />
            </button>
          </div>
          <div className="p-6 overflow-y-auto max-h-[calc(85vh-80px)]">
            {current.body()}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LegalModal;
